import { useCallback, useEffect, useRef } from 'react';

import { useGlMap } from '../../../contexts/context';

export const selectedFeatureStateKey = 'selected';
export const hoveredFeatureStateKey = 'hover';

export interface InteractiveMapLayerProps {
  sourceId;
  sourceLayer;
  layerId;
  layerStyle;
  currentStyleId?: string;
  selectedFeatureId;
  onFeatureClick;
  onFeatureHover?;
}

const InteractiveMapLayer = ({
  sourceId,
  sourceLayer,
  layerId,
  layerStyle,
  selectedFeatureId,
  onFeatureClick,
  onFeatureHover,
}: InteractiveMapLayerProps) => {
  const map = useGlMap();
  const hoveredFeatureIdRef = useRef(null);
  const selectedFeatureIdRef = useRef();
  selectedFeatureIdRef.current = selectedFeatureId;

  const setFeatureHoveredState = useCallback(
    (featureId, state) => {
      if (!map) {
        return;
      }

      if (featureId) {
        map.setFeatureState(
          {
            source: sourceId,
            sourceLayer,
            id: featureId,
          },
          { [hoveredFeatureStateKey]: state },
        );
      }
    },
    [map, sourceId, sourceLayer],
  );

  const setFeatureSelectedState = useCallback(
    (featureId, state) => {
      if (!map) {
        return;
      }

      if (featureId) {
        map.setFeatureState(
          {
            source: sourceId,
            sourceLayer,
            id: featureId,
          },
          {
            [selectedFeatureStateKey]: state,
            // required for mobile
            [hoveredFeatureStateKey]: false,
          },
        );
      }
    },
    [map, sourceId, sourceLayer],
  );

  const handleFeatureHover = useCallback(
    (e) => {
      if (!map) {
        return;
      }

      const newHoveredFeature = e.features?.[0];

      if (hoveredFeatureIdRef.current === newHoveredFeature?.id) {
        return;
      }

      if (hoveredFeatureIdRef.current) {
        setFeatureHoveredState(hoveredFeatureIdRef.current, false);
      }

      if (
        newHoveredFeature?.id &&
        newHoveredFeature.id !== selectedFeatureIdRef.current
      ) {
        setFeatureHoveredState(newHoveredFeature.id, true);
      }

      hoveredFeatureIdRef.current = newHoveredFeature?.id || null;

      const container = map.getCanvasContainer();
      if (container.style.cursor !== 'pointer') {
        container.style.cursor = 'pointer';
      }

      if (onFeatureHover) {
        onFeatureHover(newHoveredFeature);
      }
    },
    [map, onFeatureHover, setFeatureHoveredState],
  );

  const handleFeatureLeave = useCallback(() => {
    if (!map) {
      return;
    }

    if (hoveredFeatureIdRef.current !== null) {
      setFeatureHoveredState(hoveredFeatureIdRef.current, false);
    }

    hoveredFeatureIdRef.current = null;

    const container = map.getCanvasContainer();
    if (container.style.cursor) {
      (container.style.cursor as unknown) = null;
    }
  }, [map, setFeatureHoveredState]);

  const handleFeatureClick = useCallback(
    (_, features) => {
      if (!onFeatureClick) {
        return;
      }

      // filter features with ids, because between tiles may contain features with undefined ids
      const clickedFeature = features?.filter((feature) => !!feature.id)?.[0];

      if (clickedFeature) {
        onFeatureClick(clickedFeature);

        // prevent bubbling
        return false;
      }
    },
    [onFeatureClick],
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    selectedFeatureIdRef.current = selectedFeatureId || null;

    if (selectedFeatureId) {
      let timeout;
      (function trySelectFeature() {
        timeout = undefined;
        if (map.loaded()) {
          setFeatureSelectedState(selectedFeatureId, true);
        } else {
          timeout = setTimeout(trySelectFeature, 50);
        }
      })();

      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }

        setFeatureSelectedState(selectedFeatureId, false);
      };
    }
  }, [map, selectedFeatureId, setFeatureSelectedState]);

  useEffect(() => {
    if (!map) {
      return;
    }

    map.on('mousemove', layerId, handleFeatureHover);
    map.on('mouseleave', layerId, handleFeatureLeave);
    map.layerEventHandler.on('click', layerId, handleFeatureClick);

    return () => {
      map.off('mousemove', layerId, handleFeatureHover);
      map.off('mouseleave', layerId, handleFeatureLeave);
      map.layerEventHandler.off('click', layerId, handleFeatureClick);
    };
  }, [
    map,
    layerId,
    handleFeatureClick,
    handleFeatureHover,
    handleFeatureLeave,
  ]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const initializeLayer = () => {
      layerStyle.add(map);
      setFeatureSelectedState(selectedFeatureIdRef.current, true);
    };

    let timeout;
    const tryInitializeLayer = () => {
      timeout = undefined;
      if (map.loaded()) {
        initializeLayer();
      } else {
        timeout = setTimeout(tryInitializeLayer, 50);
      }
    };

    tryInitializeLayer();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      layerStyle.remove(map);
    };
  }, [map, layerStyle, setFeatureSelectedState]);

  return null;
};

export default InteractiveMapLayer;
